/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { FavoriteHeart } from 'shared-ui/favorites';
import { UIcon } from 'shared-ui/uicon';

import type { SearchResultItem } from '../api/search/search.types';
import { CardImage } from './parts/CardImage';
import { CardInventoryTag } from './parts/CardInventoryTag';
import { CardLocation, CompactCardLocation } from './parts/CardLocation';
import { CardMeta } from './parts/CardMeta';
import { CardFooter } from './parts/CardFooter';
import { CardLinkHeading } from './parts/CardLinkHeading';
import {
    CardAvailabilityBadge,
    type CardAvailability,
} from './parts/CardAvailabilityBadge';
import { useCardInteraction } from './hooks/useCardInteraction';
import { getAvailableDateRange } from './utils/getAvailableDateRange';

import styles from './SearchResultCard.module.scss';

export interface SearchResultCardProps extends React.HTMLAttributes<HTMLDivElement> {
    item: SearchResultItem;
    isActive?: boolean;
    // Adds glow for search result list exact matches
    isHighlighted?: boolean;
    hasCloseButton?: boolean;
    /** Whether to render the favorite/heart control. Defaults to `true`. */
    hasFavoriteHeart?: boolean;
    onCloseClick?: () => void;
    onHover?: (item: SearchResultItem) => void;
    onHoverLeave?: (item: SearchResultItem) => void;
    shouldFocusOnMount?: boolean;
    gaTrackingId?: string;
    gaTrackingLocation?: string;
    /** Called when the title link is clicked, before the browser opens it in a new tab */
    onLinkClick?: (item: SearchResultItem) => void;
    availability?: CardAvailability | null;
    /**
     * Show the item's first `available_dates` range in place of the availability
     * status, when it has one and `availability` is `'available'`. Other statuses
     * (first-come, lottery, ...) keep their badge, since search can attach dates
     * to those too. Defaults to `false`.
     */
    showAvailableDates?: boolean;
    appearance?: 'full' | 'compact';
}

export function SearchResultCard({
    item,
    isActive,
    isHighlighted,
    hasCloseButton,
    hasFavoriteHeart = true,
    onCloseClick,
    onHover,
    onHoverLeave,
    className,
    shouldFocusOnMount,
    gaTrackingId,
    gaTrackingLocation,
    onLinkClick,
    availability,
    showAvailableDates = false,
    appearance = 'full',
    ...rest
}: SearchResultCardProps) {
    const headingId = React.useId();
    const { cardRef, onHoverOrFocus, onHoverLeaveOrBlur } = useCardInteraction({
        item,
        onHover,
        onHoverLeave,
        shouldFocusOnMount,
    });

    // Bail out if no item is provided
    if (!item) return null;

    // Dates stand in for the status only when they mean "bookable on these dates"
    const availableDateRange =
        showAvailableDates && availability === 'available'
            ? getAvailableDateRange(item)
            : null;

    return (
        <div
            ref={cardRef}
            className={styles.searchResultCard}
            onMouseEnter={onHoverOrFocus}
            onMouseLeave={onHoverLeaveOrBlur}
            onFocus={onHoverOrFocus}
            onBlur={onHoverLeaveOrBlur}
            tabIndex={shouldFocusOnMount ? -1 : undefined}
            role={shouldFocusOnMount ? 'dialog' : undefined}
            aria-labelledby={headingId}
            {...rest}
        >
            <div
                className={cx(styles.inner, {
                    [styles.isActive]: isActive,
                    [styles.isHighlighted]: isHighlighted,
                })}
            >
                <div
                    className={cx(styles.imageWrap, {
                        [styles.isCompactImageWrap]: appearance === 'compact',
                    })}
                >
                    <CardImage item={item} className={styles.image} />

                    <div className={styles.imageButtonsGroup}>
                        <div className="left">
                            {availableDateRange ? (
                                <CardAvailabilityBadge dateRange={availableDateRange} />
                            ) : (
                                <CardAvailabilityBadge availability={availability} />
                            )}
                        </div>

                        <div className="right">
                            {hasFavoriteHeart ? (
                                <FavoriteHeart
                                    entityId={item.entity_id}
                                    entityType={item.entity_type}
                                    parentId={item.parent_id}
                                    parentType={item.parent_type}
                                    favAriaLabel={item.name}
                                    showFavoriteText={false}
                                    editable
                                />
                            ) : null}

                            {hasCloseButton ? (
                                <button
                                    type="button"
                                    className={styles.closeButton}
                                    onClick={onCloseClick}
                                    aria-label={`Close ${item.name} card`}
                                >
                                    <UIcon name="x" size={16} />
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className={styles.contentWrap}>
                    <div className={styles.topContent}>
                        <CardInventoryTag item={item} hasText />

                        <div>
                            <CardLinkHeading
                                item={item}
                                id={headingId}
                                className={styles.heading}
                                gaTrackingId={gaTrackingId}
                                gaTrackingLocation={gaTrackingLocation}
                                onLinkClick={onLinkClick}
                            />

                            {appearance === 'full' ? (
                                <CardLocation
                                    parent={item.parent_name}
                                    organization={item.org_name}
                                    city={item.city}
                                    state={item.state_code}
                                    distance={item.distance}
                                />
                            ) : (
                                <CompactCardLocation
                                    parent={item.parent_name}
                                    organization={item.org_name}
                                    city={item.city}
                                    state={item.state_code}
                                    distance={item.distance}
                                />
                            )}

                            {appearance === 'full' ? <CardMeta item={item} /> : null}
                        </div>
                    </div>

                    <CardFooter item={item} />
                </div>
            </div>
        </div>
    );
}
